import React, { useEffect, useRef } from "react";
import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";

const dateLabel = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "America/Toronto",
  });
};

/**
 * Scrollable row of matchdays (one per fixture date). Played matchdays get a tick, the
 * next one to be played is flagged "Next", and the selected one is filled.
 */
const MatchdayStrip = ({ matchdays, selectedDate, onSelect, todayKey }) => {
  const scroller = useRef(null);
  const selectedIdx = matchdays.findIndex((m) => m.date === selectedDate);
  const nextDate = matchdays.find((m) => m.date >= todayKey && m.status !== "played")?.date;

  useEffect(() => {
    const el = scroller.current?.querySelector(`[data-date="${selectedDate}"]`);
    if (el && scroller.current) {
      const box = scroller.current;
      box.scrollTo({ left: el.offsetLeft - box.clientWidth / 2 + el.clientWidth / 2, behavior: "smooth" });
    }
  }, [selectedDate]);

  if (!matchdays.length) return null;

  const step = (dir) => {
    const target = matchdays[selectedIdx + dir];
    if (target) onSelect(target.date);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: { xs: 0.5, sm: 1 }, py: 1.5 }}>
      <IconButton aria-label="Previous matchday" onClick={() => step(-1)} disabled={selectedIdx <= 0} size="small">
        <ChevronLeftIcon />
      </IconButton>
      <Box
        ref={scroller}
        role="tablist"
        aria-label="Matchdays"
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          gap: 1,
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          py: 0.5,
          px: 0.25,
          justifyContent: { md: matchdays.length <= 8 ? "center" : "flex-start" },
        }}
      >
        {matchdays.map((m) => {
          const selected = m.date === selectedDate;
          const isNext = m.date === nextDate;
          const played = m.status === "played";
          return (
            <ButtonBase
              key={m.date}
              data-date={m.date}
              role="tab"
              aria-selected={selected}
              onClick={() => onSelect(m.date)}
              sx={{
                flex: "0 0 auto",
                minWidth: 96,
                px: 1.5,
                py: 0.85,
                borderRadius: 2.5,
                flexDirection: "column",
                alignItems: "flex-start",
                border: "1px solid",
                borderColor: selected ? "#111" : isNext ? "rgba(17,17,17,0.35)" : "rgba(0,0,0,0.1)",
                bgcolor: selected ? "#111" : "#fff",
                color: selected ? "#fff" : "#111",
                transition: "background-color .15s, border-color .15s",
                "&:hover": { borderColor: "#111" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
                <Typography component="span" sx={{ fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.08em", opacity: selected ? 0.8 : 0.55 }}>
                  MD{m.matchday}
                </Typography>
                {played && <CheckIcon sx={{ fontSize: 13, color: selected ? "#86efac" : "#16a34a" }} />}
                {isNext && (
                  <Box
                    component="span"
                    sx={{
                      ml: "auto",
                      fontSize: "0.6rem",
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      px: 0.6,
                      borderRadius: 1,
                      bgcolor: selected ? "rgba(255,255,255,0.18)" : "#111",
                      color: "#fff",
                    }}
                  >
                    {m.date === todayKey ? "Today" : "Next"}
                  </Box>
                )}
              </Box>
              <Typography component="span" sx={{ fontSize: "0.82rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                {dateLabel(m.date)}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
      <IconButton
        aria-label="Next matchday"
        onClick={() => step(1)}
        disabled={selectedIdx < 0 || selectedIdx >= matchdays.length - 1}
        size="small"
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
};

export default MatchdayStrip;
